// School license pool (org admin side). Reset-only by design (D3): resetting
// unbinds a key from its holder (they're locked out at next launch and must
// sign in again with a key) and returns it to the claimable pool.
//
// V2.1: org admins also MINT seats here — up to their tier's cap
// (included_licenses). Generated keys are tier-matched, unclaimed, and appear
// in the list below as "available" to share with teachers/students.

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { orgApi } from '../../services/orgApi';
import { orgStyles } from './OrgConsole';
import LicenseStatCards from './LicenseStatCards';

const inputStyle = {
  width: '64px', padding: '7px 10px', borderRadius: '8px',
  border: '1px solid rgba(27,37,80,0.142)', background: 'rgba(27,37,80,0.057)',
  color: 'rgba(27,37,80,0.855)', fontSize: '13px', textAlign: 'center',
};

export default function LicensesTab() {
  const { t } = useTranslation('orgAdmin');
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;

  const [licenses, setLicenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prov, setProv] = useState(null);       // { seats, credits }
  const [loading, setLoading] = useState(true);
  const [confirmingReset, setConfirmingReset] = useState(null); // license id
  const [busyLicense, setBusyLicense] = useState(null);
  const [genQty, setGenQty] = useState('1');
  const [generating, setGenerating] = useState(false);
  const [newKeys, setNewKeys] = useState([]);   // transient, from the last generate

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    // Provisioning is the new (optional) header — never let it break the list.
    const [lic, p] = await Promise.allSettled([
      orgApi.listLicenses(token),
      orgApi.getProvisioning(token),
    ]);
    if (lic.status === 'fulfilled') {
      setLicenses(lic.value?.licenses || []);
      setSummary(lic.value?.summary || null);
    } else {
      toast.showError(t('licenses.toasts.loadFailed', { error: lic.reason?.message }));
    }
    setProv(p.status === 'fulfilled' ? (p.value || null) : null);
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const seatsRemaining = prov?.seats?.remaining ?? 0;

  const handleGenerate = async () => {
    if (!token || generating) return;
    const qty = parseInt(genQty, 10);
    if (!Number.isInteger(qty) || qty <= 0) { toast.showError(t('licenses.toasts.qtyMin')); return; }
    if (prov && qty > seatsRemaining) { toast.showError(t('licenses.toasts.genCap', { count: seatsRemaining })); return; }
    setGenerating(true);
    try {
      const data = await orgApi.generateLicenses(token, qty);
      const keys = data?.keys || [];
      setNewKeys(keys);
      toast.showSuccess(t('licenses.toasts.generated', { count: keys.length }), 4000);
      setGenQty('1');
      refresh();
    } catch (e) {
      const rem = e.body?.remaining;
      toast.showError(rem != null ? t('licenses.toasts.errorAvailable', { error: e.message, available: rem }) : t('licenses.toasts.generateFailed', { error: e.message }));
    } finally {
      setGenerating(false);
    }
  };

  const copyKeys = async () => {
    try { await navigator.clipboard.writeText(newKeys.join('\n')); toast.showSuccess(t('licenses.toasts.copied'), 1500); }
    catch { /* clipboard blocked — keys are visible to copy manually */ }
  };

  const handleReset = async (lic) => {
    if (!token || busyLicense) return;
    setBusyLicense(lic.id);
    try {
      await orgApi.resetLicense(token, lic.id);
      toast.showSuccess(t('licenses.toasts.keyReset', { key: lic.key }), 4000);
      setConfirmingReset(null);
      refresh();
    } catch (e) {
      toast.showError(t('licenses.toasts.resetFailed', { error: e.message }));
    } finally {
      setBusyLicense(null);
    }
  };

  const statusOf = (lic) => {
    if (lic.revoked) return { label: t('licenses.status.revoked'), color: '#FF5151' };
    if (lic.consumed) return { label: t('licenses.status.inUse'), color: '#1AB0F2' };
    return { label: t('licenses.status.available'), color: '#26C774' };
  };

  return (
    <div>
      {/* Pool summary (from the license list) */}
      <LicenseStatCards summary={summary} />

      {/* Generate control (V2.1) — cap info lives here, where it's actionable */}
      {prov && (
        <div style={{ ...orgStyles.card, marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ ...orgStyles.muted, fontSize: '13px' }}>
            {seatsRemaining > 0
              ? t('licenses.generate.canGenerate', { count: seatsRemaining })
              : t('licenses.generate.capFull')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="number" min="1" max={Math.max(seatsRemaining, 1)} value={genQty}
              onChange={(e) => setGenQty(e.target.value)}
              disabled={generating || seatsRemaining <= 0}
              style={inputStyle}
              aria-label={t('licenses.generate.qtyAria')}
            />
            <button
              style={{ ...orgStyles.btnPrimary, opacity: (generating || seatsRemaining <= 0) ? 0.5 : 1 }}
              disabled={generating || seatsRemaining <= 0}
              title={seatsRemaining <= 0 ? t('licenses.generate.capFull') : t('licenses.generate.generateTitle')}
              onClick={handleGenerate}
            >
              {generating ? t('licenses.generate.generating') : t('licenses.generate.generate')}
            </button>
          </div>
        </div>
      )}

      {/* Freshly generated keys — share these to claim */}
      {newKeys.length > 0 && (
        <div style={{ ...orgStyles.card, marginBottom: '16px', borderColor: 'rgba(127,75,246,0.45)', background: 'rgba(127,75,246,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
            <div style={{ ...orgStyles.text, fontSize: '13px', fontWeight: 600 }}>
              {t('licenses.newKeys.generated', { count: newKeys.length })}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button style={orgStyles.btn} onClick={copyKeys}>{t('licenses.newKeys.copy')}</button>
              <button style={orgStyles.btn} onClick={() => setNewKeys([])}>✕</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {newKeys.map((k) => (
              <span key={k} style={{ ...orgStyles.text, fontFamily: 'ui-monospace, monospace', letterSpacing: '1px', fontWeight: 700, fontSize: '14px', background: 'rgba(27,37,80,0.09)', padding: '4px 10px', borderRadius: '6px' }}>
                {k}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading && <div style={{ ...orgStyles.muted, padding: '24px 4px' }}>{t('loading')}</div>}
      {!loading && licenses.length === 0 && (
        <div style={{ ...orgStyles.card, ...orgStyles.muted, textAlign: 'center', padding: '36px' }}>
          {t('licenses.empty.noLicenses')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {licenses.map((lic) => {
          const st = statusOf(lic);
          const holderIsOrgAdmin = lic.consumed_by_is_org_admin === true;
          const isOwnKey = lic.consumed_by === user?.userId;
          const resettable = (lic.consumed || lic.revoked) && !holderIsOrgAdmin && !isOwnKey;
          return (
            <div key={lic.id} style={{ ...orgStyles.card, display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...orgStyles.text, fontWeight: 700, fontSize: '14px', fontFamily: 'ui-monospace, monospace', letterSpacing: '1px' }}>
                  {lic.key}
                  {lic.tier_name && <span style={{ ...orgStyles.muted, fontWeight: 400, fontFamily: 'inherit', letterSpacing: 0 }}> · {lic.tier_name}</span>}
                </div>
                <div style={{ ...orgStyles.muted, fontSize: '12px', marginTop: '2px' }}>
                  {lic.consumed
                    ? [
                        t('licenses.holder.heldBy', { name: lic.consumed_by_name || lic.consumed_by_email || t('licenses.holder.unknown') }),
                        holderIsOrgAdmin ? t('licenses.holder.orgAdmin') : null,
                        isOwnKey ? t('licenses.holder.you') : null,
                      ].filter(Boolean).join(' ')
                    : t('licenses.holder.unclaimed')}
                </div>
              </div>
              <span style={{ color: st.color, fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', flexShrink: 0 }}>
                {st.label}
              </span>
              <div style={{ flexShrink: 0, width: '200px', textAlign: 'right' }}>
                {confirmingReset === lic.id ? (
                  <div style={{ display: 'inline-flex', gap: '6px' }}>
                    <button style={orgStyles.btnDanger} disabled={busyLicense === lic.id} onClick={() => handleReset(lic)}>
                      {busyLicense === lic.id ? t('licenses.reset.resetting') : t('licenses.reset.confirm')}
                    </button>
                    <button style={orgStyles.btn} onClick={() => setConfirmingReset(null)}>✕</button>
                  </div>
                ) : resettable ? (
                  <button
                    style={orgStyles.btn}
                    title={t('licenses.reset.title')}
                    onClick={() => setConfirmingReset(lic.id)}
                  >
                    {t('licenses.reset.button')}
                  </button>
                ) : (
                  <span style={{ ...orgStyles.muted, fontSize: '12px' }}>
                    {holderIsOrgAdmin ? t('licenses.holder.adminHeld') : isOwnKey ? t('licenses.holder.yourKey') : ''}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
